import { useEffect, useState } from "react";
import { Link, NavLink, Outlet, useLocation } from "react-router-dom";
import { Menu, X, Phone } from "lucide-react";
import { ATTORNEY } from "@/data/content";
import StickyContact from "@/components/StickyContact";

const NAV = [
    { to: "/", label: "Home" },
    { to: "/about", label: "About Joe" },
    { to: "/practice-areas", label: "Practice Areas" },
    { to: "/damages", label: "Recovery" },
    { to: "/service-areas", label: "Service Areas" },
    { to: "/blog", label: "Blog" },
    { to: "/faq", label: "FAQ" },
    { to: "/contact", label: "Contact" },
];

function Nav() {
    const [open, setOpen] = useState(false);
    const { pathname } = useLocation();

    useEffect(() => {
        setOpen(false);
        window.scrollTo({ top: 0, behavior: "instant" });
    }, [pathname]);

    return (
        <header
            data-testid="site-header"
            className="sticky top-0 z-50 backdrop-blur-xl bg-[#07142B]/85 border-b border-[#1F3458]"
        >
            <div className="max-w-[1400px] mx-auto px-6 lg:px-12 h-20 flex items-center justify-between gap-6">
                <Link
                    to="/"
                    data-testid="nav-logo"
                    className="group flex items-center gap-3"
                >
                    <span className="h-9 w-9 grid place-items-center border border-[#D4AF37] text-[#D4AF37] font-serif text-xl">
                        H
                    </span>
                    <span className="flex flex-col leading-tight">
                        <span className="font-serif text-lg text-[#F7F5EB] tracking-tight">
                            Joseph G. Helfrick
                        </span>
                        <span className="text-[10px] uppercase tracking-[0.22em] text-[#D4AF37]">
                            Personal Injury · Sacramento
                        </span>
                    </span>
                </Link>

                <nav
                    className="hidden lg:flex items-center gap-5"
                    aria-label="Primary"
                >
                    {NAV.map((n) => (
                        <NavLink
                            key={n.to}
                            to={n.to}
                            data-testid={`nav-link-${n.to.replace(/\//g, "") || "home"}`}
                            end={n.to === "/"}
                            className={({ isActive }) =>
                                `relative text-sm font-medium tracking-wide transition-colors ${
                                    isActive
                                        ? "text-[#D4AF37]"
                                        : "text-[#F7F5EB]/85 hover:text-[#D4AF37]"
                                }`
                            }
                        >
                            {({ isActive }) => (
                                <>
                                    <span>{n.label}</span>
                                    {isActive && (
                                        <span className="absolute -bottom-[26px] left-0 right-0 h-[2px] bg-[#D4AF37]" />
                                    )}
                                </>
                            )}
                        </NavLink>
                    ))}
                </nav>

                <div className="hidden lg:flex items-center gap-4">
                    <a
                        data-testid="nav-phone"
                        href={`tel:${ATTORNEY.phone.replace(/[^0-9]/g, "")}`}
                        className="flex items-center gap-2 text-[#D4AF37] text-sm font-semibold"
                    >
                        <Phone size={14} />
                        {ATTORNEY.phone}
                    </a>
                    <Link
                        to="/contact"
                        data-testid="nav-cta"
                        className="bg-[#D4AF37] text-[#07142B] font-bold uppercase tracking-wider text-[11px] px-5 py-3 hover:bg-[#B8962A] transition-colors"
                    >
                        Free Consultation
                    </Link>
                </div>

                <button
                    data-testid="nav-mobile-toggle"
                    aria-label="Open menu"
                    onClick={() => setOpen((o) => !o)}
                    className="lg:hidden text-[#F7F5EB]"
                >
                    {open ? <X size={26} /> : <Menu size={26} />}
                </button>
            </div>

            {open && (
                <div
                    data-testid="mobile-menu"
                    className="lg:hidden border-t border-[#1F3458] bg-[#07142B]"
                >
                    <div className="px-6 py-6 flex flex-col gap-4">
                        {NAV.map((n) => (
                            <NavLink
                                key={n.to}
                                to={n.to}
                                end={n.to === "/"}
                                data-testid={`nav-mobile-link-${n.to.replace(/\//g, "") || "home"}`}
                                className={({ isActive }) =>
                                    `text-base ${
                                        isActive
                                            ? "text-[#D4AF37]"
                                            : "text-[#F7F5EB]"
                                    }`
                                }
                            >
                                {n.label}
                            </NavLink>
                        ))}
                        <a
                            href={`tel:${ATTORNEY.phone.replace(/[^0-9]/g, "")}`}
                            data-testid="nav-mobile-phone"
                            className="text-[#D4AF37] font-semibold"
                        >
                            Call {ATTORNEY.phone}
                        </a>
                        <Link
                            to="/contact"
                            data-testid="nav-mobile-cta"
                            className="bg-[#D4AF37] text-[#07142B] font-bold uppercase tracking-wider text-xs px-5 py-3 w-fit"
                        >
                            Free Consultation
                        </Link>
                    </div>
                </div>
            )}
        </header>
    );
}

function Footer() {
    return (
        <footer
            data-testid="site-footer"
            className="mt-24 border-t border-[#1F3458] bg-[#07142B]"
        >
            <div className="max-w-[1400px] mx-auto px-6 lg:px-12 py-16 grid md:grid-cols-12 gap-12">
                <div className="md:col-span-5">
                    <div className="font-serif text-3xl text-[#F7F5EB] leading-tight">
                        Joseph G. Helfrick
                    </div>
                    <div className="text-[#D4AF37] text-xs uppercase tracking-[0.22em] mt-1">
                        Personal Injury Attorney
                    </div>
                    <p className="mt-6 text-[#9FB3D4] text-sm leading-relaxed max-w-md">
                        Representing the seriously injured throughout Sacramento
                        County, Placer County, and Northern California. Free,
                        confidential case review — you pay nothing unless we
                        win.
                    </p>
                </div>

                <div className="md:col-span-3">
                    <div className="text-[#D4AF37] text-[11px] uppercase tracking-[0.22em] mb-4">
                        Site
                    </div>
                    <ul className="space-y-2 text-sm text-[#F7F5EB]/85">
                        {NAV.map((n) => (
                            <li key={n.to}>
                                <Link
                                    to={n.to}
                                    data-testid={`footer-link-${n.to.replace(/\//g, "") || "home"}`}
                                    className="hover:text-[#D4AF37] transition-colors"
                                >
                                    {n.label}
                                </Link>
                            </li>
                        ))}
                        <li>
                            <Link
                                to="/case-evaluator"
                                data-testid="footer-link-case-evaluator"
                                className="hover:text-[#D4AF37] transition-colors"
                            >
                                60-Second Case Evaluator
                            </Link>
                        </li>
                    </ul>
                </div>

                <div className="md:col-span-4">
                    <div className="text-[#D4AF37] text-[11px] uppercase tracking-[0.22em] mb-4">
                        Contact
                    </div>
                    <ul className="space-y-2 text-sm text-[#F7F5EB]/85">
                        <li>
                            <a
                                href={`tel:${ATTORNEY.phone.replace(/[^0-9]/g, "")}`}
                                data-testid="footer-phone"
                                className="hover:text-[#D4AF37]"
                            >
                                {ATTORNEY.phone}
                            </a>
                        </li>
                        <li>
                            <a
                                href={`mailto:${ATTORNEY.email}`}
                                data-testid="footer-email"
                                className="hover:text-[#D4AF37] break-all"
                            >
                                {ATTORNEY.email}
                            </a>
                        </li>
                        <li className="text-[#9FB3D4]">
                            {ATTORNEY.address.street}
                            <br />
                            {ATTORNEY.address.city}, {ATTORNEY.address.region}{" "}
                            {ATTORNEY.address.postal}
                        </li>
                    </ul>
                </div>
            </div>

            {/* LEGAL PARTNERS */}
            <div className="border-t border-[#1F3458]">
                <div className="max-w-[1400px] mx-auto px-6 lg:px-12 py-10 flex flex-col md:flex-row items-start md:items-center gap-6 md:gap-10">
                    <div className="text-[#D4AF37] text-[11px] uppercase tracking-[0.22em] font-bold shrink-0">
                        Legal Partners
                    </div>
                    <a
                        href="https://ltglaw.net/"
                        target="_blank"
                        rel="noopener noreferrer"
                        data-testid="footer-legal-partner"
                        className="group flex items-center gap-5 hover:opacity-90 transition-opacity"
                        aria-label="Lauria Tokunaga Gates & Linn — Attorneys at Law (opens in new tab)"
                    >
                        <img
                            src="/images/ltg-logo.png"
                            alt="Lauria Tokunaga Gates & Linn LLP — Attorneys at Law"
                            className="h-16 md:h-20 w-auto border border-[#1F3458] group-hover:border-[#D4AF37] transition-colors"
                            loading="lazy"
                        />
                        <div className="hidden sm:block">
                            <div className="font-serif text-lg text-[#F7F5EB] leading-tight">
                                Lauria Tokunaga Gates &amp; Linn, LLP
                            </div>
                            <div className="text-[#9FB3D4] text-xs mt-1">
                                Joe's firm — full-service civil litigation in
                                Northern California.{" "}
                                <span className="text-[#D4AF37] group-hover:underline">
                                    Visit ltglaw.net →
                                </span>
                            </div>
                        </div>
                    </a>
                </div>
            </div>

            <div className="border-t border-[#1F3458]">
                <div className="max-w-[1400px] mx-auto px-6 lg:px-12 py-6 flex flex-col md:flex-row gap-3 justify-between text-[11px] text-[#9FB3D4]">
                    <div>
                        © {new Date().getFullYear()} Joseph G. Helfrick. All
                        rights reserved.
                    </div>
                    <div className="max-w-3xl">
                        Attorney advertising. The information on this website is
                        for general informational purposes only and does not
                        constitute legal advice. Contacting us does not create
                        an attorney-client relationship. Past results do not
                        guarantee future outcomes.
                    </div>
                </div>
            </div>
        </footer>
    );
}

export default function Layout() {
    return (
        <div className="min-h-screen flex flex-col bg-[#07142B] text-[#F7F5EB]">
            <Nav />
            <main className="flex-1">
                <Outlet />
            </main>
            <Footer />
            {/* Spacer so the mobile sticky bar never covers footer content */}
            <div className="lg:hidden h-16" aria-hidden />
            <StickyContact />
        </div>
    );
}
